import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { INSPECTION_COMMENTS } from "@/constants/Inspections";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import { clone } from "@/util/clone";
import { useInspectionStore } from "@/stores/inspection";
import { getFormattedDateStringNoTime } from "@/composables/dateField.js";
import { CUSTOM_ICONS } from "@/constants/Unicode";
import { getScourPOA } from "@/composables/waterways.js";
import { floodPlainCustomSort } from "@/composables/featuresWaterway";
import { sortByTwoProperties } from "@/util/sortStructure.js";

function setWaterwayDocDefinition(structure) {
  const inspectionStore = useInspectionStore();
  const configStore = useConfigStore();

  const currentScourMeasuresAndCountermeasures = [];
  const potentailCountermeasures = [];
  const subUnitsOne = [];
  const subUnitsTwo = [];

  processCurrentScourMeasuresAndCountermeasures();
  processPotentailCountermeasures();
  let floodPlains = getFloodPlains(structure, inspectionStore);
  floodPlains?.sort(floodPlainCustomSort);
  processSubUnitsOne();
  subUnitsTwo.push(getSubUnitsTableTwo());

  let scourCriticalCategory = getScourCriticalCategory(structure);

  let scourPOARequired = getScourPOA(scourCriticalCategory);

  let overallScourHole = inspectionStore.getOverallScourHole(
    structure?.userInspection?.scourCondRating,
    null,
    structure,
  );

  let underwaterRating = getUnderwaterRating(structure);

  let docDefinition = {
    header: function () {
      return getReportHeader("Waterway Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Waterway Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Waterway Conditions",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        columns: [
          {
            width: "25%",
            text: "WA.01 SCBI: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.SCOUR_CRITICAL_BRIDGE_INDICATOR,
              structure?.inspection?.scourCritical,
            ),
            margin: [0, 3, 0, 0],
          },
          {
            width: "35%",
            text: "B.C.09 Channel: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.chanRating),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "WA.02 Scour Critical Category: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: scourCriticalCategory,
            margin: [0, 3, 0, 0],
          },
          {
            width: "35%",
            text: "B.C.10 Channel Protection: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.chanProtRating,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "WA.03 SCBI Source: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.SCOUR_CRITICAL_BRIDGE_INDICATOR_SOURCE,
              structure?.scourComp?.scbiSource,
            ),
            margin: [0, 3, 0, 0],
          },
          {
            width: "35%",
            text: "B.C.11 Overall Scour: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.SCOUR_CONDITION_RATING, overallScourHole),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "WA.04 Overall SCBI: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.SCOUR_CRITICAL_BRIDGE_INDICATOR,
              structure?.scourComp?.overallOSA,
            ),
            margin: [0, 3, 0, 0],
          },
          {
            width: "35%",
            text: "B.C.15 Underwater: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, underwaterRating),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "WA.05 SCBI Recalculated?: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.scourComp?.scourCalculated == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
            style: "customFontAwesome",
            margin: [0, 3, 0, 0],
          },
          {
            width: "35%",
            text: "B.N.06 Substructure Nav Protection: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.PIER_PROTECTION, structure?.inspection?.pierprot),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "",
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 3, 0, 0],
          },
          {
            width: "35%",
            text: "B.AP.03 Scour Vulnerability: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.SCOUR_VULNERABILITY,
              structure?.userInspection?.scourVulnerability,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "25%",
            text: "",
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 3, 0, 0],
          },
          {
            width: "35%",
            text: "B.AP.04 Scour POA Required: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.SCOUR_POA_REQUIRED, scourPOARequired),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Waterway Details",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "35%",
            text: "WA.06 Stream Bed Material: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.STREAM_BED_MATERIAL,
              structure?.scourComp?.streamBedMaterial1,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.STREAM_BED_MATERIAL,
              structure?.scourComp?.streamBedMaterial2,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "WA.07 Streambed Material Description:",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.scourComp?.streamBedMaterialDesc,
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "WA.08 Number of Subunits: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "15%",
            text: structure?.scourComp?.unitsInspectedQty,
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "WA.15 Horizontal Debris Start: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "7%",
            text: structure?.scourComp?.horDebrisStartPct
              ? Number.parseFloat(structure?.scourComp?.horDebrisStartPct).toFixed(0)
              : "0",
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "WA.09 NAB Location: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "15%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.ABUTMENT_LOCATION, structure?.scourComp?.nabLocation),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "WA.16 Horizontal Debris End: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "7%",
            text: structure?.scourComp?.horDebrisEndPct
              ? Number.parseFloat(structure?.scourComp?.horDebrisEndPct).toFixed(0)
              : "0",
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "(0%=LAB to 100%=RAB)",
            margin: [3, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "WA.10 FAB Location: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "15%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.ABUTMENT_LOCATION, structure?.scourComp?.fabLocation),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "WA.17 Vertical Debris Start: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "7%",
            text: structure?.scourComp?.vertDebrisStartPct
              ? Number.parseFloat(structure?.scourComp?.vertDebrisStartPct).toFixed(0)
              : "0",
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "WA.11 U/S Left WW Presence: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "15%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.WINGWALL_PRESENCE,
              structure?.scourComp?.usLeftWWPresence,
            ),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "WA.18 Vertical Debris End: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "7%",
            text: structure?.scourComp?.vertDebrisEndPct
              ? Number.parseFloat(structure?.scourComp?.vertDebrisEndPct).toFixed(0)
              : "0",
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "(0%=Str'bed to 100%=Bm)",
            margin: [3, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "WA.12 U/S Left Condition: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "15%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.UPSTREAM_WINGWALL_CONDITION,
              structure?.scourComp?.usLeftWWCond,
            ),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "WA.13 U/S Right WW Presence: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "15%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.WINGWALL_PRESENCE,
              structure?.scourComp?.usRightWWPresence,
            ),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "WA.14 U/S Right Condition: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "15%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.UPSTREAM_WINGWALL_CONDITION,
              structure?.scourComp?.usRightWWCond,
            ),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 3, 0, 0],
          },
        ],
        pageBreak: "after",
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Current Scour Measures and Countermeasures",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", "*", "*", "*"],
          body: currentScourMeasuresAndCountermeasures,
        },
        margin: [0, 5, 0, 0],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Potential Countermeasures",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", "*"],
          body: potentailCountermeasures,
        },
        margin: [0, 5, 0, 0],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Channel and Waterway Adequacy Information",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "13%",
            text: "B.C.09 Channel: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.chanRating),
            margin: [0, 3, 0, 0],
          },
          {
            width: "22%",
            text: "B.C.10 Channel Protection: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.chanProtRating,
            ),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        text: "Channel: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find((element) => element?.commentType == INSPECTION_COMMENTS.CHANNEL)
          ?.notes,
      },
      {
        text: "Channel Protection: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.CHANNEL_PROTECTION,
        )?.notes,
      },
      {
        text: "Banks: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find((element) => element?.commentType == INSPECTION_COMMENTS.BANKS)
          ?.notes,
      },
      {
        text: "Streambed Movements: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.STREAMBED_MOVEMENTS,
        )?.notes,
      },
      {
        text: "Debris, Vegetation: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.DEBRIS_VEGITATION,
        )?.notes,
      },
      {
        text: "River (Stream) Control Devices: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.RIVER_CONTROL_DEVICES,
        )?.notes,
      },
      {
        text: "Embankment/Streambed Controls: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.EMBANKMENT_STREAMBED_CONTROLS,
        )?.notes,
      },
      {
        text: "Drift, Other: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find((element) => element?.commentType == INSPECTION_COMMENTS.DRIFT_OTHER)
          ?.notes,
      },
      {
        text: "Waterway Adequacy: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.WATERWAY_ADEQUACY,
        )?.notes,
      },
      {
        columns: [
          {
            width: "35%",
            text: "B.AP.02 Overtopping Likelihood: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.OVERTOPPING_RISK, structure?.scourComp?.overTopRisk),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "WA.54 Traffic Delay: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.TRAFFIC_DELAY, structure?.scourComp?.trafficDelay),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "WA.55 Worst Flood Event: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.WORST_FLOOD_EVENT, structure?.scourComp?.overTopping),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "WA.56 Worst Flood Event Date: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: getFormattedDateStringNoTime(structure?.scourComp?.overtopDate),
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "B.H.01 Functional Class:",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.ROADWAY_FUNCTIONAL_CLASSIFICATION,
              structure?.structureRoadways?.find((a) => a?.onUnder === "1").funcClass,
            ),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "",
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "24%",
            text: "WA.57 High Water Elevation: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "10%",
            text: structure?.scourComp?.highWaterElev
              ? Number.parseInt(structure?.scourComp?.highWaterElev) + " ft"
              : " ft",
            margin: [0, 3, 0, 0],
          },
          {
            width: "20%",
            text: "WA.58 High Water Date: ",
            bold: true,
            margin: [3, 3, 0, 0],
          },
          {
            width: "13%",
            text: getFormattedDateStringNoTime(structure?.scourComp?.highWaterDate),
            margin: [0, 3, 0, 0],
          },
          {
            width: "25%",
            text: "WA.59 New High Water Mark: ",
            bold: true,
            margin: [3, 3, 0, 0],
          },
          {
            width: "8%",
            text: structure?.scourComp?.newHighWater == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
            style: "customFontAwesome",
            margin: [3, 3, 0, 0],
          },
        ],
      },
      {
        text: "WA.60 High Water Notes: ",
        bold: true,
        margin: [0, 3, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.HIGH_WATER_MARK,
        )?.notes,
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Subunits within 500yr Flood Plain",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        table: {
          widths: ["100%"],
          headerRows: 0,
          body: [
            ["Legend:"],
            [
              {
                table: {
                  headerRows: 0,
                  widths: ["23%", "31%", "23%", "23%"],
                  body: [
                    ["WA.27 Subunit", "WA.34 Debris Potential", "WA.38 Alignment", "WA.46 Movement Ind"],
                    ["WA.31 OBS Scour Rtg", "WA.35 Scourability", "WA.39 Velocity Str Slope", "WA.29 SCBI Code"],
                    ["WA.32 Chg Since Last Insp", "WA.36 Opening Adeq/Channel", "WA.40 Pier/Abut Type", ""],
                    ["WA.33 Scour Hole", "WA.37 Sediment", "WA.43 Strmbd Matl", ""],
                  ],
                },
                layout: "noBorders",
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function (i, node) {
            return hLineFunction(i, node.table.body.length);
          },
          vLineWidth: function (i, node) {
            return hLineFunction(i, node.table.widths.length);
          },
          hLineColor: function () {
            return "black";
          },
          vLineColor: function () {
            return "black";
          },
        },
        unbreakable: true,
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", "*", "*", "*", "*", "*", "*", "*", "*", "*", "*", "*", "*", "*"],
          body: subUnitsOne,
        },
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "20%",
            text: "Other Subunit Details: ",
            bold: true,
            margin: [0, 5, 0, 5],
          },
          {
            width: "*",
            text: "",
            margin: [0, 5, 0, 5],
          },
        ],
      },
      {
        table: {
          widths: ["100%"],
          headerRows: 0,
          body: [
            ["Legend:"],
            [
              {
                table: {
                  headerRows: 0,
                  widths: ["25%", "21%", "22%", "32%"],
                  body: [
                    [
                      "WA.27 Subunit",
                      "WA.44 UW Insp Type",
                      "WA.48 Scour/Undermine",
                      "WA.50 100yr Flood Calc Scour Depth",
                    ],
                    [
                      "WA.28 Info From Current Insp",
                      "WA.45 Obs Scr Depth",
                      "WA.49 Countermeasures",
                      "WA.51 500yr Flood Calc Scour Depth",
                    ],
                    ["WA.42 OSA Found Type", "WA.46 Water Depth", "", ""],
                  ],
                },
                layout: "noBorders",
              },
            ],
          ],
        },
        layout: {
          hLineWidth: function (i, node) {
            return hLineFunction(i, node.table.body.length);
          },
          vLineWidth: function (i, node) {
            return hLineFunction(i, node.table.widths.length);
          },
          hLineColor: function () {
            return "black";
          },
          vLineColor: function () {
            return "black";
          },
        },
        unbreakable: true,
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", "*", "*", "*", "*", "*", "*", "*", "*", "*", "*"],
          body: subUnitsTwo,
        },
        margin: [0, 5, 0, 5],
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  for (const floodPlain of floodPlains ?? []) {
    let subUnitsThree = [];
    let subUnitsFour = [];
    subUnitsThree.push([
      {
        text: floodPlain?.subUnit,
        style: ["tableBody"],
      },

      {
        text: floodPlain?.foundType,
        style: ["tableBody"],
      },
      {
        text: floodPlain?.underwaterInspType,
        style: ["tableBody"],
      },
      {
        text: floodPlain.maxWaterDepth,
        style: ["tableBody"],
      },
      {
        text: floodPlain.obsScourDepth,
        style: ["tableBody"],
      },
      {
        text: inspectionStore.getReportCheckbox(floodPlain.scourUndermine),
        style: ["customFontAwesomeTableBody"],
      },
      {
        text: inspectionStore.getReportCheckbox(floodPlain.counterMeasure),
        style: ["customFontAwesomeTableBody"],
      },
      {
        text: inspectionStore.getReportCheckbox(floodPlain.previousCurrent),
        style: ["customFontAwesomeTableBody"],
      },
      {
        text: floodPlain.calcScourDepth100,
        style: ["tableBody"],
      },
      {
        text: floodPlain.calcScourDepth500,
        style: ["tableBody"],
      },
    ]);
    subUnitsFour.push([
      {
        text: ["WA.53 Notes: ", floodPlain?.underwaterInspDesc],
        style: ["waterwayLeftAlignBody"],
      },
    ]);
    docDefinition.content.push(
      {
        table: {
          headerRows: 1,
          widths: ["*", "*", "*", "*", "*", "*", "*", "*", "*", "*", "*"],
          body: subUnitsThree,
        },
        margin: [0, 0, 0, 0],
      },
      {
        table: {
          headerRows: 1,
          widths: ["*"],
          body: subUnitsFour,
        },
        margin: [0, 0, 0, 0],
      },
    );
  }
  function processCurrentScourMeasuresAndCountermeasures() {
    currentScourMeasuresAndCountermeasures.push(getCurrentScourMeasuresAndCountermeasuresHeader());

    const measuresCurrent = getMeasuresCurrent(structure);
    for (const [idx, measure] of (measuresCurrent ?? []).entries()) {
      measure.UNIQUE_KEY = idx + 1;
      measure.readOnly = true;
      currentScourMeasuresAndCountermeasures.push([
        {
          text: configStore.getReferenceValue(REFERENCE_TABLE.CURRENT_COUNTERMEASURE_TYPE, measure.cMeasureType, false),
          style: ["tableBody"],
        },
        {
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.CURRENT_COUNTERMEASURE_LOCATION,
            measure.cMeasureLocation,
            false,
          ),
          style: ["tableBody"],
        },
        {
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.CURRENT_COUNTERMEASURE_CONDITION,
            measure.cMeasureCond,
            false,
          ),
          style: ["tableBody"],
        },
        {
          text: inspectionStore.getSubUnit(measure.pierNum),
          style: ["tableBody"],
        },
      ]);
    }
  }

  function processPotentailCountermeasures() {
    potentailCountermeasures.push(getPotentailCountermeasuresHeader());

    const measuresPotential = getMeasuresPotential(structure);
    for (const [idx, measure] of (measuresPotential ?? []).entries()) {
      measure.UNIQUE_KEY = idx + 1;

      potentailCountermeasures.push([
        {
          text: configStore.getReferenceValue(REFERENCE_TABLE.CURRENT_COUNTERMEASURE_LOCATION, measure.location),
          style: ["tableBody"],
        },
        {
          text: configStore.getReferenceValue(REFERENCE_TABLE.WORK_CANDIDATE, measure.wcKey),
          style: ["tableBody"],
        },
      ]);
    }
  }

  function processSubUnitsOne() {
    subUnitsOne.push(getSubUnitsTableOne());
    for (const floodPlain of floodPlains ?? []) {
      subUnitsOne.push([
        {
          text: floodPlain.subUnit,
          style: ["tableBody"],
        },
        {
          text: floodPlain.subUnitType,
          style: ["tableBody"],
        },
        {
          text: floodPlain.streamBedMaterial,
          style: ["tableBody"],
        },
        {
          text: inspectionStore.getReportCheckbox(floodPlain.movement),
          style: ["customFontAwesomeTableBody"],
        },
        {
          text: floodPlain.changeSinceLastInsp,
          style: ["tableBody"],
        },
        {
          text: floodPlain.scourHole,
          style: ["tableBody"],
        },
        {
          text: floodPlain.debrisPotential,
          style: ["tableBody"],
        },
        {
          text: floodPlain.subScour,
          style: ["tableBody"],
        },
        {
          text: floodPlain.openAdeqChannel,
          style: ["tableBody"],
        },
        {
          text: floodPlain.sedDeposit,
          style: ["tableBody"],
        },
        {
          text: floodPlain.alignment,
          style: ["tableBody"],
        },
        {
          text: floodPlain.veloStreamSlope,
          style: ["tableBody"],
        },
        {
          text: floodPlain.obsScourRating,
          style: ["tableBody"],
        },
        {
          text: floodPlain.scbiCode,
          style: ["tableBody"],
        },
      ]);
    }
  }

  return docDefinition;
}

function getCurrentScourMeasuresAndCountermeasuresHeader() {
  return [
    {
      text: "WA.20\n Type",
      style: ["tableHeader"],
    },
    {
      text: "WA.21\n Location",
      style: ["tableHeader"],
    },
    {
      text: "WA.22\n Condition",
      style: ["tableHeader"],
    },
    {
      text: "WA.23\n Subunit",
      style: ["tableHeader"],
    },
  ];
}

function getPotentailCountermeasuresHeader() {
  return [
    {
      text: "WA.25\n Location",
      style: ["tableHeader"],
    },
    {
      text: "WA.26\n Countermeasures",
      style: ["tableHeader"],
    },
  ];
}

function getSubUnitsTableOne() {
  return [
    {
      text: "WA.27",
      style: ["tableHeader"],
    },
    {
      text: "WA.40",
      style: ["tableHeader"],
    },
    {
      text: "WA.43",
      style: ["tableHeader"],
    },
    {
      text: "WA.47",
      style: ["tableHeader"],
    },
    {
      text: "WA.32",
      style: ["tableHeader"],
    },
    {
      text: "WA.33",
      style: ["tableHeader"],
    },
    {
      text: "WA.34",
      style: ["tableHeader"],
    },
    {
      text: "WA.35",
      style: ["tableHeader"],
    },
    {
      text: "WA.36",
      style: ["tableHeader"],
    },
    {
      text: "WA.37",
      style: ["tableHeader"],
    },
    {
      text: "WA.38",
      style: ["tableHeader"],
    },
    {
      text: "WA.39",
      style: ["tableHeader"],
    },
    {
      text: "WA.31",
      style: ["tableHeader"],
    },
    {
      text: "WA.29",
      style: ["tableHeader"],
    },
  ];
}

function getSubUnitsTableTwo() {
  return [
    {
      text: "WA.27",
      style: ["tableHeader"],
    },
    {
      text: "WA.42",
      style: ["tableHeader"],
    },
    {
      text: "WA.44",
      style: ["tableHeader"],
    },
    {
      text: "WA.46",
      style: ["tableHeader"],
    },
    {
      text: "WA.45",
      style: ["tableHeader"],
    },
    {
      text: "WA.48",
      style: ["tableHeader"],
    },
    {
      text: "WA.49",
      style: ["tableHeader"],
    },
    {
      text: "WA.28",
      style: ["tableHeader"],
    },
    {
      text: "WA.50",
      style: ["tableHeader"],
    },
    {
      text: "WA.51",
      style: ["tableHeader"],
    },
  ];
}

function getUnderwaterRating(structure) {
  let underwaterRating = null;
  if (structure?.userInspection?.underwaterRating) {
    underwaterRating = structure?.userInspection?.underwaterRating;
  } else if (structure?.inspection?.subRating < structure?.inspection?.culvRating) {
    underwaterRating = structure?.inspection?.subRating;
  } else {
    underwaterRating = structure?.inspection?.culvRating;
  }
  return underwaterRating;
}

function getScourCriticalCategory(structure) {
  let scourCriticalCategory = structure?.inspection?.scourCriticalCat;
  if (!scourCriticalCategory || scourCriticalCategory == "") {
    scourCriticalCategory = "--";
  }
  return scourCriticalCategory;
}

function getFloodPlains(structure, inspectionStore) {
  let floodPlains = clone(structure.inspectionsUnderwater);
  //remove null values from array(if any)
  floodPlains = floodPlains?.filter((a) => !!a) || [];

  for (let idx = 1; idx <= floodPlains?.length; idx++) {
    floodPlains[idx - 1].UNIQUE_KEY = idx;
    let subLookup = inspectionStore.getSubStructure(floodPlains[idx - 1]?.strUnitKey);
    floodPlains[idx - 1].subUnit = subLookup?.strUnitLabel;
    floodPlains[idx - 1].legacyStructureType = subLookup?.legacyStructureType;
  }
  return floodPlains || [];
}

function getMeasuresCurrent(structure) {
  let measuresCurrent = clone(structure.scourCMeasures);

  if (!measuresCurrent?.[0]) {
    //remove null values from array(if any)
    measuresCurrent = measuresCurrent?.filter((a) => !!a);
  }
  sortByTwoProperties(measuresCurrent, "cMeasureLocation", "pierNum");
  return measuresCurrent;
}

function getMeasuresPotential(structure) {
  let measuresPotential = clone(structure.scourPossCMeasures);
  if (measuresPotential?.[0]) {
    //remove null values from array(if any)
    measuresPotential = measuresPotential?.filter((a) => !!a);
  }
  sortByTwoProperties(measuresPotential, "location", "wcKey");
  return measuresPotential;
}

function hLineFunction(i, value) {
  return i === 0 || i === value ? 1 : 0;
}

export { setWaterwayDocDefinition };
